import { observer } from "mobx-react-lite"
import { CruiseDetailsPlaceholder } from "@cruisen/react-shared"
import { SearchResultDetails } from "@cruisen/contract"
import { getTripDatesInfo } from "@cruisen/react-shared"
import { useStores } from "../../../models/helpers/useStores"
import { useEffect, useMemo, useRef } from "react"
import { updateTrip, updateTripSearch } from "../../../services/firestore/trips"
import { useCruiseSearchResult } from "../../../services/firestore/cruiseSearchResult"
import { useMutation, useQuery } from "@tanstack/react-query"
import { getCruiseDetails } from "../../../services/firestore/cruiseDetails"
import { TripCard } from "./cards/CruiseDetailsTripCard"
import styles from "./styles.module.scss"
import { ShipCard } from "./cards/CruiseDetailsShipCard"
import { makeAutoObservable } from "mobx"
import { getSearchCriteriaHash } from "../../../models/helpers/getSearchCriteriaHash"
import CloseOutline from "~icons/eva/close-outline"
import LinkOutline from "~icons/eva/link-2-outline"
import { CruiseDetailsHero } from "./CruiseDetailsHero"
import classNames from "classnames"
import { CabinsCard } from "./cards/CruiseDetailsCabinCard"
import { DecksCard } from "./cards/CruiseDetailsDecksCard"
import Tag from "../../atoms/Tag"
import { Analytics } from "../../../utils/analytics"
import { CruiseDetailsFooter } from "./CruiseDetailsFooter"
import { getPaymentRequestList } from "../../molecules/Room/utils"
import { invalidatePaymentRequests } from "../../../services/firebase-functions/billing"
import CloseModalButton from "../../buttons/CloseModalButton"
import { Link } from "react-router-dom"
import Config from "../../../config"
import { ConfirmationModal } from "../../modals/ConfirmationModal"

class CruiseDetailsState {
  isHeaderVisible: boolean
  activeModal: string | null

  constructor() {
    this.isHeaderVisible = false
    this.activeModal = null

    makeAutoObservable(this)
  }

  setIsHeaderVisible(value: boolean) {
    this.isHeaderVisible = value
  }

  setActiveModal(value: string | null) {
    this.activeModal = value
  }
}

const transformPlaceholderIntoCruiseDetails = (placeholderData: CruiseDetailsPlaceholder) => {
  return {
    tripSearchResult: {
      name: placeholderData?.name || "",
      lowestPrice: placeholderData?.lowestPrice || 0,
      lowestPricePP: placeholderData?.lowestPricePP || { price: 0, adults: 0, children: 0 },
      startDate: placeholderData?.startDate || null,
      endDate: placeholderData?.endDate || null,
      likes: (placeholderData?.likes as string[]) || [],
      lineLogo: placeholderData?.lineLogo || "",
      lineName: placeholderData?.lineName || "",
      cabinsScanStatus: "waiting",
      cabinGradesScanStatus: "waiting",
      cabinGradeTypes: [],
    },
    itineraryItems: [],
    ship: {
      name: placeholderData?.name || "",
      imageUrl: placeholderData?.imageUrl || "",
      images: [{ url: placeholderData?.imageUrl || "" }],
      description: "",
    },
    decks: [],
    cabinGrades: [],
  } as SearchResultDetails
}

const showHeaderPosition = 400

export const CruiseDetails = observer(function CruiseDetails() {
  const tripSearchId = "main"
  const rootStore = useStores()
  const { activeTrip, cruiseDetailsPlaceholderData, loggedInUser } = rootStore
  const tripId = activeTrip.id
  const cruiseId = cruiseDetailsPlaceholderData?.id

  const state = useMemo(() => new CruiseDetailsState(), [cruiseId])

  const scrollRef = useRef(null)
  const hasAnyRoomActivePaymentRequest = activeTrip.rooms.some((room) => {
    const paymentRequestList = getPaymentRequestList(room)
    return paymentRequestList.some(
      (paymentRequest) =>
        paymentRequest.status !== "cancelledByAgent" && paymentRequest.status !== "rejectedByUser",
    )
  })

  const { mutateAsync: mutateInvalidatePaymentRequests, isPending: isInvalidatingPaymentRequests } =
    useMutation({
      mutationFn: async (tripId: string) => {
        await invalidatePaymentRequests({ tripId })
        await updateTrip(tripId, {
          chosenTripSearchResultId: "",
          updatedBy: loggedInUser.id,
        })
      },
      onSuccess: () => {
        state.setActiveModal(null)
      },
    })

  useEffect(() => {
    scrollRef?.current?.scrollTo({
      top: 0,
      behavior: "instant",
    })
  }, [cruiseId])

  const handleScroll = () => {
    if (scrollRef.current) {
      const { scrollTop } = scrollRef.current
      state.setIsHeaderVisible(scrollTop >= showHeaderPosition)
    }
  }

  const chosenTripSearchResultId = activeTrip.chosenTripSearchResultId

  useEffect(() => {
    Analytics.logViewItem({ name: cruiseDetails.tripSearchResult.name, id: cruiseId })
  }, [cruiseId])

  useEffect(() => {
    if (!cruiseId) return
    updateTripSearch(tripId, tripSearchId, {
      lastViewedResultId: cruiseId,
    }).catch((error) => console.error("update trip last viewed cabins failed", error))
  }, [tripId, tripSearchId, cruiseId])

  const cruiseDetailsQueryKey = [
    "cruiseDetails",
    tripId,
    getSearchCriteriaHash(activeTrip),
    cruiseId,
  ]

  const {
    data: cruiseDetailsData,
    isLoading,
    isError,
  } = useQuery({
    queryKey: cruiseDetailsQueryKey,
    queryFn: () => getCruiseDetails(tripId, cruiseId, tripSearchId),
    enabled: !!cruiseId,
  })

  const cruiseQuery = useCruiseSearchResult(tripId, tripSearchId, cruiseId)

  const { mutateAsync: mutateSetPreferredCruise, isPending: isSettingPreferredCruise } =
    useMutation({
      mutationFn: async (setPreferred: boolean) => {
        await updateTrip(tripId, {
          chosenTripSearchResultId: setPreferred ? cruiseSearchResult.id : "",
          updatedBy: loggedInUser.id,
        })
        Analytics.logSetPreferredCruise({ cruiseId: cruiseSearchResult.id })
      },
    })

  if (cruiseDetailsData === null || isError) {
    /** @TODO error handling */
    //Alert.alert(_t("cruiseDetailsScreen.errors.failedToLoad"))
    console.error("cruise details load failure")
    return null
  }

  if (!cruiseId) return null

  const cruiseDetails = isLoading
    ? transformPlaceholderIntoCruiseDetails(cruiseDetailsPlaceholderData)
    : cruiseDetailsData

  const { ship, itineraryItems } = cruiseDetails

  const cruiseQueryData = cruiseQuery.data
  const isLoadingData = cruiseQueryData?.status === "isLoading" || isLoading
  const cruiseSearchResult = cruiseQueryData?.cruiseSearchResult ?? cruiseDetails.tripSearchResult

  const heroImages = ship.images.sort(({ url }) => (url === ship.imageUrl ? -1 : 1))

  const decks = cruiseQueryData?.decks?.length ? cruiseQueryData.decks : cruiseDetails.decks
  const cabinGrades = cruiseQueryData?.cabinGrades?.length
    ? cruiseQueryData.cabinGrades
    : cruiseDetails.cabinGrades

  const isSomeRoomBooked =
    activeTrip.rooms.some((room) => room.bookingDetails !== null) && activeTrip.rooms.length > 0

  const isCruiseLikedByAgent = cruiseDetails.tripSearchResult.likes.some(
    (like) => like === rootStore.loggedInUser.id,
  )

  const closeCruise = async () => {
    rootStore.setProp("cruiseDetailsPlaceholderData", null)
  }
  return (
    <div className={styles.container}>
      <div
        className={classNames(
          styles.cruiseDetails,
          !isSomeRoomBooked && styles.cruiseDetailsWithFooter,
        )}
        ref={scrollRef}
        onScroll={handleScroll}
        data-testid="cruise-details"
      >
        <div className={styles.carouselContainer}>
          <div className={styles.tagList}>
            {isCruiseLikedByAgent ? <Tag variant="agentPick" /> : null}
            {cruiseSearchResult.tags?.includes("priceDrop") ? <Tag variant="priceDrop" /> : null}
          </div>
          <CruiseDetailsHero images={heroImages} />
          {Config.environment !== "production" && (
            <Link
              className={styles.cruiseLink}
              to={`/trips/${tripId}/cruises/${cruiseId}`}
              target="_blank"
            >
              <LinkOutline className={styles.linkIcon} />
            </Link>
          )}
          <button
            className={styles.backButton}
            onClick={() => closeCruise()}
            data-test-id="cruise-details-x-button"
          >
            <CloseOutline className={styles.closeIcon} />
          </button>
        </div>
        <div className={styles.cruiseLineLogoContainer}>
          <img
            src={cruiseDetails.tripSearchResult.lineLogo}
            className={styles.cruiseLineLogo}
            alt={cruiseDetails.tripSearchResult.lineName}
          />
        </div>
        <div className={styles.title}>
          <p className={styles.cruiseSailingName}>{cruiseDetails.tripSearchResult.name}</p>
          <p className={styles.cruiseLineName}>{cruiseDetails.tripSearchResult.lineName}</p>
        </div>

        <div className={styles.cardsList}>
          <TripCard
            itineraryItems={itineraryItems}
            isLoadingData={isLoadingData}
            tripDatesInfo={getTripDatesInfo(
              cruiseSearchResult.startDate,
              cruiseSearchResult.endDate,
            )}
          />
          {!isLoadingData && (
            <>
              <ShipCard ship={ship} />
              <DecksCard decks={decks} />
              <CabinsCard
                cabinGradeTypes={cruiseSearchResult.cabinGradeTypes}
                cabinGrades={cabinGrades}
                cabinsScanStatus={cruiseSearchResult.cabinsScanStatus}
                cabinGradesScanStatus={cruiseSearchResult.cabinGradesScanStatus}
                cabinGradeDiscounts={cruiseSearchResult.cabinGradeDiscounts}
              />
            </>
          )}
        </div>
      </div>
      <div
        className={classNames(
          styles.header,
          state.isHeaderVisible ? styles.visible : styles.hidden,
        )}
      >
        <div className={styles.filler}></div>
        <p>
          {cruiseSearchResult?.name
            ? cruiseSearchResult.name.length > 25
              ? `${cruiseSearchResult.name.slice(0, 25)}...`
              : cruiseSearchResult.name
            : ""}
        </p>
        <CloseModalButton onClick={closeCruise} />
      </div>
      {!isLoadingData && !isSomeRoomBooked && (
        <div className={styles.footerContainer}>
          <CruiseDetailsFooter
            cruise={cruiseSearchResult}
            isSelected={cruiseId === chosenTripSearchResultId}
            onClick={async () => {
              const isSelected = cruiseId === chosenTripSearchResultId
              const shouldShowModal = localStorage.getItem("dontShowSelectCruiseModal") !== "true"

              if (shouldShowModal && !isSelected) {
                state.setActiveModal("selectCruiseModal")
                return
              }

              if (isSelected && hasAnyRoomActivePaymentRequest) {
                state.setActiveModal("invalidatePaymentRequestsModal")
                return
              }

              await mutateSetPreferredCruise(cruiseId !== chosenTripSearchResultId)
              if (!isSelected) {
                closeCruise()
              }
            }}
          />
        </div>
      )}
      {state.activeModal === "selectCruiseModal" ? (
        <ConfirmationModal
          title="Select cruise"
          description="Selecting this cruise will set it as the choice for your entire trip, visible to all participants."
          onConfirm={async () => {
            const isSelected = cruiseId === chosenTripSearchResultId
            await mutateSetPreferredCruise(cruiseId !== chosenTripSearchResultId)
            state.setActiveModal(null)
            if (!isSelected) {
              closeCruise()
            }
          }}
          closeModal={() => state.setActiveModal(null)}
          isLoading={isSettingPreferredCruise}
          doNotShowText="dontShowSelectCruiseModal"
          confirmButtonText={isSettingPreferredCruise ? "Selecting..." : "Select"}
          testId="select-cruise-modal"
          isPrimary={true}
          singleButton={true}
        />
      ) : null}
      {state.activeModal === "invalidatePaymentRequestsModal" ? (
        <ConfirmationModal
          title="Invalidate quotes"
          description="Unselecting this cruise will invalidate all existing quotes. You will need to create new quotes after a different cruise is selected."
          onConfirm={() => {
            mutateInvalidatePaymentRequests(tripId)
          }}
          closeModal={() => state.setActiveModal(null)}
          isLoading={isInvalidatingPaymentRequests}
          confirmButtonText={isInvalidatingPaymentRequests ? "Unselecting..." : "Unselect"}
          testId="invalidate-payment-requests-modal"
          isPrimary={true}
        />
      ) : null}
    </div>
  )
})
